import { Skeleton } from "../Skeleton";

export interface ResultsCountProps {
  /** Number of results/items */
  count: number;
  /** Label to use (e.g., "results", "items", "sites") */
  label?: string;
  /** Whether the count is loading */
  isLoading?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * ResultsCount
 *
 * Displays a count of results or items with proper singular/plural handling.
 * Shows a loading skeleton when data is being fetched.
 *
 * Example usage:
 * ```tsx
 * <ResultsCount count={23} label="results" />
 * // Renders: "23 results"
 *
 * <ResultsCount count={1} label="site" />
 * // Renders: "1 site"
 *
 * <ResultsCount count={42} label="items" isLoading={true} />
 * // Renders: loading skeleton
 * ```
 */
export function ResultsCount({ count, label = "results", isLoading = false, className = "" }: ResultsCountProps) {
  if (isLoading) {
    return (
      <div className={`flex items-center ${className}`}>
        <Skeleton width={80} height={16} />
      </div>
    );
  }

  // Handle singular/plural
  const singularLabel = label.endsWith("s") ? label.slice(0, -1) : label;
  const displayLabel = count === 1 ? singularLabel : label;

  return (
    <div className={`text-sm text-text-muted whitespace-nowrap ${className}`}>
      {count.toLocaleString()} {displayLabel}
    </div>
  );
}
